import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Modal,
  Card,
  CardMedia,
  CardContent,
  Grid,
  CircularProgress,
  alpha,
  useTheme,
  IconButton,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BugReportIcon from '@mui/icons-material/BugReport';
import HelpIcon from '@mui/icons-material/Help';
import ImageIcon from '@mui/icons-material/Image';
import MapIcon from '@mui/icons-material/Map';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';

// Import API service for authenticated requests
import api from '../../../services/api';
import { chainageApi } from '../../../services/api';
import AuthenticatedImage from '../../common/AuthenticatedImage'; // Import the new component

const DuplicationModal = ({
  open,
  onClose,
  selectedRowForDuplicates,
  duplicateImages,
  loadingDuplicates,
  formatDateTime,
}) => {
  const theme = useTheme();
  const [selectedDuplicateIndex, setSelectedDuplicateIndex] = useState(0);
  const [originalBoundingBox, setOriginalBoundingBox] = useState(null);
  const [loadingOriginalBoundingBox, setLoadingOriginalBoundingBox] = useState(false);

  // View toggle state (image or map)
  const [currentView, setCurrentView] = useState('image');
  
  // Map state
  const [mapInstance, setMapInstance] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapCleanedUp, setMapCleanedUp] = useState(false);
  const mapContainerRef = useRef(null);
  
  // Chainage segment state
  const [chainageSegmentData, setChainageSegmentData] = useState(null);
  const [chainageSegmentLayer, setChainageSegmentLayer] = useState(null);
  const [loadingChainageSegment, setLoadingChainageSegment] = useState(false);
  
  // Configuration state for dynamic distance threshold
  const [distanceThreshold, setDistanceThreshold] = useState(20); // Default fallback
  const [loadingConfig, setLoadingConfig] = useState(false);
  
  // Persist chainage segment data across view changes
  const chainageSegmentDataRef = useRef(null);

  // Zoom and pan state for Original Image
  const [originalZoom, setOriginalZoom] = useState({ scale: 1, translateX: 0, translateY: 0 });
  const [originalIsPanning, setOriginalIsPanning] = useState(false);
  const [originalPanStart, setOriginalPanStart] = useState({ x: 0, y: 0 });
  const originalImageRef = useRef(null);
  const originalImageContainerRef = useRef(null);

  // Zoom and pan state for Selected Duplicate Image
  const [duplicateZoom, setDuplicateZoom] = useState({ scale: 1, translateX: 0, translateY: 0 });
  const [duplicateIsPanning, setDuplicateIsPanning] = useState(false);
  const [duplicatePanStart, setDuplicatePanStart] = useState({ x: 0, y: 0 });
  const duplicateImageRef = useRef(null);
  const duplicateImageContainerRef = useRef(null);

  // Get the currently selected duplicate image
  const selectedDuplicate = duplicateImages[selectedDuplicateIndex];

  // Fetch configuration data including distance threshold
  const fetchConfiguration = async () => {
    if (loadingConfig) return; // Prevent multiple calls
    
    setLoadingConfig(true);
    try {
      console.log('Fetching burst detection configuration...');
      const response = await api.get('/payload/burst-config');
      const config = response.data.config;
      
      console.log('Configuration received:', config);
      
      if (config && config.distanceThresholdMeters) {
        setDistanceThreshold(config.distanceThresholdMeters);
        console.log(`Distance threshold updated to: ${config.distanceThresholdMeters}m`);
      }
    } catch (error) {
      console.error('Error fetching configuration:', error);
      // Keep default fallback value (20) if fetch fails
      console.warn('Using fallback distance threshold: 20m');
    } finally {
      setLoadingConfig(false);
    }
  };

  // Fetch configuration when modal opens
  useEffect(() => {
    if (open) {
      fetchConfiguration();
    }
  }, [open]);

  // Fetch original bounding box data when modal opens or selectedRowForDuplicates changes
  useEffect(() => {
    const fetchOriginalBoundingBox = async () => {
      if (!selectedRowForDuplicates || !selectedRowForDuplicates.originalBoundingBoxId) {
        setOriginalBoundingBox(null);
        return;
      }

      setLoadingOriginalBoundingBox(true);
      try {
        console.log('Fetching original bounding box for ID:', selectedRowForDuplicates.originalBoundingBoxId);
        const response = await api.get(`/envis-findings/bounding-boxes/${selectedRowForDuplicates.originalBoundingBoxId}`);
        console.log('Original bounding box data received:', response.data);
        setOriginalBoundingBox(response.data);
        
        // Fetch chainage segment if available
        if (response.data.matched_chainage_segment_id) {
          fetchChainageSegmentData(response.data.matched_chainage_segment_id);
        }
      } catch (error) {
        console.error('Error fetching original bounding box data:', error);
        setOriginalBoundingBox(null);
      } finally {
        setLoadingOriginalBoundingBox(false);
      }
    };

    if (open && selectedRowForDuplicates) {
      fetchOriginalBoundingBox();
    }
  }, [open, selectedRowForDuplicates]);

  const fetchChainageSegmentData = async (chainageSegmentId) => {
    if (!chainageSegmentId) return;
    
    setLoadingChainageSegment(true);
    try {
      console.log('Fetching chainage segment data for ID:', chainageSegmentId);
      
      // Fetch chainage segment geometry as GeoJSON
      const response = await chainageApi.getChainageSegmentGeometry(chainageSegmentId);
      const data = response.data;
      
      console.log('Chainage segment data received:', data);
      setChainageSegmentData(data);
      
      // Also store in ref for persistence across view changes
      chainageSegmentDataRef.current = data;
      
      // Add to map if map is already initialized
      if (mapInstance && data.geometry) {
        console.log('Adding chainage segment to existing map');
        addChainageSegmentToMap(data);
      } else {
        console.log('Map not ready, chainage segment will be added when map initializes');
      }
    } catch (error) {
      console.error('Error fetching chainage segment data:', error);
      // Don't show error to user, just log it - chainage segment is optional
    } finally {
      setLoadingChainageSegment(false);
    }
  };

  const addChainageSegmentToMap = async (chainageData) => {
    if (!mapInstance || !chainageData?.geometry) {
      console.log('Cannot add chainage segment - missing map or geometry:', {
        hasMap: !!mapInstance,
        hasGeometry: !!chainageData?.geometry
      });
      return;
    }
    
    try {
      console.log('Adding chainage segment to map...', chainageData);
      
      // Dynamically import Leaflet
      const L = await import('leaflet');
      
      // Remove existing chainage segment layer if it exists and is still on the map
      if (chainageSegmentLayer) {
        try {
          console.log('Removing existing chainage segment layer');
          mapInstance.removeLayer(chainageSegmentLayer);
        } catch (error) {
          console.warn('Error removing existing chainage segment layer (might already be removed):', error);
        }
        setChainageSegmentLayer(null);
      }
      
      // Create GeoJSON layer for the chainage segment
      const geoJsonLayer = L.geoJSON(chainageData.geometry, {
        style: {
          color: '#2196F3',
          weight: 3,
          opacity: 0.9,
          fillColor: '#2196F3',
          fillOpacity: 0.2,
          dashArray: '5, 10'
        },
        onEachFeature: (feature, layer) => {
          // Add popup with chainage segment info
          if (chainageData.name || chainageData.code || chainageData.km_marker) {
            const popupContent = `
              <div style="font-family: Arial, sans-serif;">
                <strong style="color: #2196F3;">Chainage Segment</strong><br/>
                ${chainageData.name ? `<strong>Name:</strong> ${chainageData.name}<br/>` : ''}
                ${chainageData.code ? `<strong>Code:</strong> ${chainageData.code}<br/>` : ''}
                ${chainageData.km_marker ? `<strong>KM:</strong> ${chainageData.km_marker}<br/>` : ''}
                <em style="color: #666; font-size: 0.9em;">ID: ${chainageData.id}</em>
              </div>
            `;
            layer.bindPopup(popupContent, {
              maxWidth: 250,
              className: 'chainage-segment-popup'
            });
          }
        }
      });
      
      // Add to map
      geoJsonLayer.addTo(mapInstance);
      setChainageSegmentLayer(geoJsonLayer);
      
      console.log('Chainage segment successfully added to map');
      
      // Try to fit the map to show both defect and chainage segment
      try {
        const defectLatLng = [
          originalBoundingBox?.latitude || selectedRowForDuplicates?.rawData?.latitude || 3.1390,
          originalBoundingBox?.longitude || selectedRowForDuplicates?.rawData?.longitude || 101.6869
        ];
        
        // Get bounds of chainage segment
        const chainageBounds = geoJsonLayer.getBounds();
        
        // Get all existing markers on the map
        const allMapMarkers = [];
        mapInstance.eachLayer((layer) => {
          if (layer instanceof L.Marker) {
            allMapMarkers.push(layer);
          }
        });
        
        // Extend bounds to include all markers
        if (allMapMarkers.length > 0) {
          allMapMarkers.forEach(marker => {
            chainageBounds.extend(marker.getLatLng());
          });
        } else {
          // Fallback: extend to include defect location
          const defectLatLngObj = L.latLng(defectLatLng[0], defectLatLng[1]);
          chainageBounds.extend(defectLatLngObj);
        }
        
        // Fit map to show everything with padding
        mapInstance.fitBounds(chainageBounds, { padding: [20, 20] });
        
        console.log('Map fitted to show chainage segment and all markers');
      } catch (boundsError) {
        console.warn('Error fitting map bounds:', boundsError);
      }
      
    } catch (error) {
      console.error('Error adding chainage segment to map:', error);
    }
  };

  const cleanupMap = (reason = 'unknown') => {
    console.log('Cleaning up map, reason:', reason);
    let shouldCleanup = false;
    
    // Check if we have a map instance or container with Leaflet state
    if (mapInstance && !mapCleanedUp) {
      shouldCleanup = true;
    }
    
    if (mapContainerRef.current && mapContainerRef.current._leaflet_id) {
      shouldCleanup = true;
    }
    
    if (!shouldCleanup) {
      console.log('No cleanup needed');
      return;
    }
    
    // Remove chainage segment layer if it exists
    if (chainageSegmentLayer && mapInstance) {
      try {
        console.log('Removing chainage segment layer');
        mapInstance.removeLayer(chainageSegmentLayer);
      } catch (error) {
        console.warn('Error removing chainage segment layer:', error);
      }
    }
    
    // Remove map instance if it exists
    if (mapInstance) {
      try {
        // Check if map container still exists and is connected
        const container = mapInstance.getContainer();
        if (container && container.parentNode) {
          console.log('Removing map instance');
          mapInstance.remove();
        } else {
          console.log('Map container already disconnected, skipping removal');
        }
      } catch (error) {
        console.warn('Error during map cleanup:', error);
        // Continue with cleanup even if removal fails
      }
    }
    
    // Clean up container completely
    if (mapContainerRef.current) {
      const container = mapContainerRef.current;
      
      // Remove Leaflet's internal state
      if (container._leaflet_id) {
        delete container._leaflet_id;
      }
      
      // Remove all Leaflet-related classes
      container.className = container.className
        .split(' ')
        .filter(cls => !cls.startsWith('leaflet-'))
        .join(' ');
      
      // Reset all attributes and styles
      container.removeAttribute('tabindex');
      container.removeAttribute('style');
      container.innerHTML = '';
      container.style.cssText = '';
    }
    
    // Reset state - but preserve chainageSegmentData
    setMapInstance(null);
    setMapCleanedUp(true);
    setChainageSegmentLayer(null);
    console.log('Map cleanup complete, chainage data preserved:', !!chainageSegmentData);
    // Note: chainageSegmentData is intentionally NOT reset here
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      console.log(`Switching view from ${currentView} to ${newView}`);
      
      // Clean up map when switching away from map view
      if (currentView === 'map' && newView !== 'map') {
        console.log('Switching away from map, cleaning up...');
        cleanupMap('view change');
      }
      
      setCurrentView(newView);
      
      // Initialize map when switching to map view
      if (newView === 'map') {
        console.log('Switching to map view, initializing map...');
        console.log('Chainage segment data available:', !!chainageSegmentData);
        setMapCleanedUp(false);
        // Add a longer delay to ensure proper cleanup completion
        setTimeout(() => {
          initializeMap();
        }, 200);
      }
    }
  };

  const initializeMap = async () => {
    if (!mapContainerRef.current) {
      console.log('Map container not available');
      return;
    }
    
    if (mapInstance) {
      console.log('Map instance already exists, skipping initialization');
      return;
    }
    
    setMapLoading(true);
    
    try {
      // Clear the container completely to ensure clean state
      if (mapContainerRef.current) {
        const container = mapContainerRef.current;
        
        // Check for any remaining Leaflet state
        if (container._leaflet_id) {
          console.log('Removing stale leaflet ID');
          delete container._leaflet_id;
        }
        
        // Remove any remaining Leaflet classes
        container.className = container.className
          .split(' ')
          .filter(cls => !cls.startsWith('leaflet-'))
          .join(' ');
        
        // Reset all attributes and styles
        container.removeAttribute('tabindex');
        container.removeAttribute('style');
        container.innerHTML = '';
        container.style.cssText = '';
      }
      
      // Dynamically import Leaflet
      const L = await import('leaflet');
      
      // Get coordinates from originalBoundingBox or selectedRowForDuplicates
      const latitude = originalBoundingBox?.latitude || selectedRowForDuplicates?.rawData?.latitude || 3.1390; // Default to Kuala Lumpur
      const longitude = originalBoundingBox?.longitude || selectedRowForDuplicates?.rawData?.longitude || 101.6869;
      
      // Create map instance with fresh container
      const map = L.map(mapContainerRef.current, {
        center: [latitude, longitude],
        zoom: 16,
        zoomControl: true,
        preferCanvas: false,
        attributionControl: false,
      });
      
      // Try Stadia Maps first, with fallback to OpenStreetMap
      const stadiaLayer = L.tileLayer('https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png?api_key=948eb013-bbdc-42c1-98a5-6e6f0fede1d1', {
        maxZoom: 20,
        attribution: '',
      });
      
      // Fallback tile layer
      const osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: ''
      });
      
      // Add error handling for tile loading
      stadiaLayer.on('tileerror', (error) => {
        console.warn('Stadia Maps tile error, falling back to OSM:', error);
        map.removeLayer(stadiaLayer);
        osmLayer.addTo(map);
      });
      
      // Add the primary layer
      stadiaLayer.addTo(map);
      
      // Create custom defect icon for original
      const defectIcon = L.divIcon({
        className: 'defect-marker',
        html: `
          <div style="
            background-color: #f44336;
            border: 3px solid white;
            border-radius: 50%;
            width: 16px;
            height: 16px;
            box-shadow: 0 2px 8px rgba(244, 67, 54, 0.4);
            cursor: pointer;
          "></div>
        `,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });
      
      // Create custom duplicate icon (orange color)
      const duplicateIcon = L.divIcon({
        className: 'duplicate-marker',
        html: `
          <div style="
            background-color: #ff9800;
            border: 3px solid white;
            border-radius: 50%;
            width: 14px;
            height: 14px;
            box-shadow: 0 2px 8px rgba(255, 152, 0, 0.4);
            cursor: pointer;
          "></div>
        `,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      });
      
      // Create selected duplicate icon (larger and more prominent)
      const selectedDuplicateIcon = L.divIcon({
        className: 'selected-duplicate-marker',
        html: `
          <div style="
            background-color: #ff9800;
            border: 4px solid #fff;
            border-radius: 50%;
            width: 18px;
            height: 18px;
            box-shadow: 0 3px 12px rgba(255, 152, 0, 0.6);
            cursor: pointer;
            position: relative;
          ">
            <div style="
              position: absolute;
              top: -2px;
              left: -2px;
              right: -2px;
              bottom: -2px;
              border: 2px solid #ff9800;
              border-radius: 50%;
              animation: pulse 2s infinite;
            "></div>
          </div>
          <style>
            @keyframes pulse {
              0% { transform: scale(1); opacity: 1; }
              50% { transform: scale(1.2); opacity: 0.7; }
              100% { transform: scale(1); opacity: 1; }
            }
          </style>
        `,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });
      
      // Add marker for original defect location
      const originalMarker = L.marker([latitude, longitude], { icon: defectIcon }).addTo(map);
      
      // Add popup for original marker
      originalMarker.bindPopup(`
        <div style="font-family: Arial, sans-serif;">
          <strong style="color: #f44336;">Original Detection</strong><br/>
          <strong>Type:</strong> ${selectedRowForDuplicates.defectType}<br/>
          <strong>Device:</strong> ${selectedRowForDuplicates.deviceId}<br/>
          <strong>Location:</strong> ${selectedRowForDuplicates.location}<br/>
          <strong>Detected:</strong> ${formatDateTime ? formatDateTime(selectedRowForDuplicates.detectedAt) : 'N/A'}<br/>
          <em style="color: #666; font-size: 0.9em;">ID: ${selectedRowForDuplicates.recordType === 'confirmed_defect' ? `D-${selectedRowForDuplicates.defectId}` : `F-${selectedRowForDuplicates.id}`}</em>
        </div>
      `, {
        maxWidth: 300,
        className: 'original-defect-popup'
      });
      
      // Add dynamic threshold circle around original defect
      const thresholdCircle = L.circle([latitude, longitude], {
        radius: distanceThreshold, // Dynamic radius from configuration
        color: '#f44336',
        weight: 2,
        opacity: 0.7,
        fillColor: '#f44336',
        fillOpacity: 0.1,
        dashArray: '5, 10',
        interactive: false // Make it non-clickable
      }).addTo(map);
      
      // Add popup to the threshold circle
      thresholdCircle.bindPopup(`
        <div style="font-family: Arial, sans-serif; text-align: center;">
          <strong style="color: #f44336;">Duplicate Detection Threshold</strong><br/>
          <span style="color: #666;">Radius: ${distanceThreshold} meters</span><br/>
          <span style="color: #666; font-size: 0.9em;">Detections within this area are considered duplicates</span>
        </div>
      `, {
        maxWidth: 250,
        className: 'threshold-circle-popup'
      });
      
      // Create an array to store all markers for bounds calculation
      const allMarkers = [originalMarker];
      
      // Add markers for all duplicate findings
      if (duplicateImages && duplicateImages.length > 0) {
        duplicateImages.forEach((duplicate, index) => {
          // Check if duplicate has coordinates
          if (duplicate.latitude && duplicate.longitude) {
            // Choose icon based on whether this duplicate is currently selected
            const isSelected = index === selectedDuplicateIndex;
            const markerIcon = isSelected ? selectedDuplicateIcon : duplicateIcon;
            
            const duplicateMarker = L.marker([duplicate.latitude, duplicate.longitude], { icon: markerIcon }).addTo(map);
            
            // Add popup for duplicate marker
            duplicateMarker.bindPopup(`
              <div style="font-family: Arial, sans-serif;">
                <strong style="color: #ff9800;">Duplicate #${index + 1}</strong><br/>
                <strong>Confidence:</strong> ${(duplicate.confidence_score * 100).toFixed(1)}%<br/>
                <strong>Distance:</strong> ${duplicate.distance_from_confirmed_defect_meters?.toFixed(1) || 'N/A'}m<br/>
                <strong>Device:</strong> ${duplicate.device_name || duplicate.device_uuid || 'Unknown'}<br/>
                <strong>Detected:</strong> ${formatDateTime ? formatDateTime(duplicate.detected_at || duplicate.created_at || duplicate.first_detected_at) : 'N/A'}<br/>
                <em style="color: #666; font-size: 0.9em;">ID: F-${duplicate.id}</em>
              </div>
            `, {
              maxWidth: 300,
              className: 'duplicate-defect-popup'
            });
            
            // Add click event to update selected duplicate
            duplicateMarker.on('click', () => {
              console.log(`Duplicate marker #${index + 1} clicked, updating selection`);
              setSelectedDuplicateIndex(index);
            });
            
            allMarkers.push(duplicateMarker);
            
            // Create dotted line connecting duplicate to original
            const connectionLine = L.polyline(
              [
                [latitude, longitude], // Original defect position
                [duplicate.latitude, duplicate.longitude] // Duplicate position
              ],
              {
                color: '#9e9e9e',
                weight: 2,
                opacity: 0.7,
                dashArray: '8, 6',
                interactive: false // Lines are not clickable
              }
            ).addTo(map);
            
            // Add popup to the line showing distance
            connectionLine.bindPopup(`
              <div style="font-family: Arial, sans-serif; text-align: center;">
                <strong style="color: #9e9e9e;">Connection</strong><br/>
                <span style="color: #666;">Distance: ${duplicate.distance_from_confirmed_defect_meters?.toFixed(1) || 'N/A'}m</span><br/>
                <span style="color: #666; font-size: 0.9em;">Duplicate #${index + 1} → Original</span>
              </div>
            `, {
              maxWidth: 200,
              className: 'connection-line-popup'
            });
          }
        });
      }
      
      // Force map to refresh/invalidate size after container is ready
      setTimeout(() => {
        try {
          map.invalidateSize();
          
          // Fit map bounds to show all markers
          if (allMarkers.length > 1) {
            // If we have multiple markers, fit to show all
            const group = new L.featureGroup(allMarkers);
            map.fitBounds(group.getBounds().pad(0.1)); // Add 10% padding
          } else if (allMarkers.length === 1) {
            // If only original marker, center on it with good zoom level
            map.setView([latitude, longitude], 16);
          }
        } catch (error) {
          console.warn('Error invalidating map size or fitting bounds:', error);
        }
      }, 200);
      
      // Store map instance AFTER everything is set up
      setMapInstance(map);
      
      // Add chainage segment if data is available (check both state and ref)
      const availableChainageData = chainageSegmentData || chainageSegmentDataRef.current;
      if (availableChainageData) {
        console.log('Map initialized, adding existing chainage segment data');
        // Use setTimeout to ensure map is fully ready before adding chainage segment
        setTimeout(() => {
          addChainageSegmentToMap(availableChainageData);
        }, 100);
      } else {
        console.log('Map initialized, no chainage segment data available yet');
      }
      
      // Add map click handler for future features
      map.on('click', (e) => {
        console.log('Map clicked at:', e.latlng);
      });
      
    } catch (error) {
      console.error('Failed to initialize map:', error);
      setMapLoading(false);
    } finally {
      setTimeout(() => {
        setMapLoading(false);
      }, 500);
    }
  };

  // Cleanup map when component unmounts or modal closes
  useEffect(() => {
    return () => {
      if (mapInstance && !mapCleanedUp) {
        cleanupMap('component unmount');
      }
    };
  }, [mapInstance, mapCleanedUp]);

  // Cleanup map when modal closes
  useEffect(() => {
    if (!open && mapInstance && !mapCleanedUp) {
      cleanupMap('modal close');
    }
  }, [open, mapInstance, mapCleanedUp]);

  // Add chainage segment to map when data becomes available
  useEffect(() => {
    const availableChainageData = chainageSegmentData || chainageSegmentDataRef.current;
    if (mapInstance && availableChainageData && availableChainageData.geometry) {
      console.log('Chainage segment data available, adding to map');
      // Always try to add chainage segment when map is available and data exists
      // The addChainageSegmentToMap function will handle removing existing layers
      addChainageSegmentToMap(availableChainageData);
    }
  }, [mapInstance, chainageSegmentData]);

  // Re-initialize map when distance threshold changes
  useEffect(() => {
    if (mapInstance && currentView === 'map' && distanceThreshold !== 20) {
      console.log(`Distance threshold changed to ${distanceThreshold}m, reinitializing map...`);
      cleanupMap('threshold change');
      setTimeout(() => {
        initializeMap();
      }, 200);
    }
  }, [distanceThreshold]);

  // Update map markers when selected duplicate changes
  useEffect(() => {
    if (mapInstance && currentView === 'map' && duplicateImages && duplicateImages.length > 0) {
      console.log('Selected duplicate changed, updating map markers');
      // Clear existing markers and re-add them with updated selection state
      mapInstance.eachLayer((layer) => {
        if (layer.options && (layer.options.icon?.options?.className === 'duplicate-marker' || 
                              layer.options.icon?.options?.className === 'selected-duplicate-marker')) {
          mapInstance.removeLayer(layer);
        }
      });
      
      // Re-add duplicate markers with updated selection
      const L = window.L; // Assuming Leaflet is already loaded
      if (L) {
        duplicateImages.forEach((duplicate, index) => {
          if (duplicate.latitude && duplicate.longitude) {
            const isSelected = index === selectedDuplicateIndex;
            
            // Recreate icons
            const duplicateIcon = L.divIcon({
              className: 'duplicate-marker',
              html: `
                <div style="
                  background-color: #ff9800;
                  border: 3px solid white;
                  border-radius: 50%;
                  width: 14px;
                  height: 14px;
                  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.4);
                  cursor: pointer;
                "></div>
              `,
              iconSize: [14, 14],
              iconAnchor: [7, 7],
            });
            
            const selectedDuplicateIcon = L.divIcon({
              className: 'selected-duplicate-marker',
              html: `
                <div style="
                  background-color: #ff9800;
                  border: 4px solid #fff;
                  border-radius: 50%;
                  width: 18px;
                  height: 18px;
                  box-shadow: 0 3px 12px rgba(255, 152, 0, 0.6);
                  cursor: pointer;
                  position: relative;
                ">
                  <div style="
                    position: absolute;
                    top: -2px;
                    left: -2px;
                    right: -2px;
                    bottom: -2px;
                    border: 2px solid #ff9800;
                    border-radius: 50%;
                    animation: pulse 2s infinite;
                  "></div>
                </div>
                <style>
                  @keyframes pulse {
                    0% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.2); opacity: 0.7; }
                    100% { transform: scale(1); opacity: 1; }
                  }
                </style>
              `,
              iconSize: [18, 18],
              iconAnchor: [9, 9],
            });
            
            const markerIcon = isSelected ? selectedDuplicateIcon : duplicateIcon;
            const duplicateMarker = L.marker([duplicate.latitude, duplicate.longitude], { icon: markerIcon }).addTo(mapInstance);
            
            // Add popup
            duplicateMarker.bindPopup(`
              <div style="font-family: Arial, sans-serif;">
                <strong style="color: #ff9800;">Duplicate #${index + 1}</strong><br/>
                <strong>Confidence:</strong> ${(duplicate.confidence_score * 100).toFixed(1)}%<br/>
                <strong>Distance:</strong> ${duplicate.distance_from_confirmed_defect_meters?.toFixed(1) || 'N/A'}m<br/>
                <strong>Device:</strong> ${duplicate.device_name || duplicate.device_uuid || 'Unknown'}<br/>
                <strong>Detected:</strong> ${formatDateTime ? formatDateTime(duplicate.detected_at || duplicate.created_at || duplicate.first_detected_at) : 'N/A'}<br/>
                <em style="color: #666; font-size: 0.9em;">ID: F-${duplicate.id}</em>
              </div>
            `, {
              maxWidth: 300,
              className: 'duplicate-defect-popup'
            });
            
            // Add click event
            duplicateMarker.on('click', () => {
              console.log(`Duplicate marker #${index + 1} clicked, updating selection`);
              setSelectedDuplicateIndex(index);
            });
          }
        });
      }
    }
  }, [selectedDuplicateIndex, mapInstance, currentView, duplicateImages, formatDateTime]);

  // Mouse event handlers for panning - Original Image
  React.useEffect(() => {
    if (originalIsPanning) {
      document.addEventListener('mousemove', originalHandlePanMove);
      document.addEventListener('mouseup', originalHandlePanEnd);
      
      return () => {
        document.removeEventListener('mousemove', originalHandlePanMove);
        document.removeEventListener('mouseup', originalHandlePanEnd);
      };
    }
  }, [originalIsPanning, originalPanStart]);

  // Mouse event handlers for panning - Duplicate Image
  React.useEffect(() => {
    if (duplicateIsPanning) {
      document.addEventListener('mousemove', duplicateHandlePanMove);
      document.addEventListener('mouseup', duplicateHandlePanEnd);
      
      return () => {
        document.removeEventListener('mousemove', duplicateHandlePanMove);
        document.removeEventListener('mouseup', duplicateHandlePanEnd);
      };
    }
  }, [duplicateIsPanning, duplicatePanStart]);

  // Reset zoom when selected duplicate changes
  React.useEffect(() => {
    setDuplicateZoom({ scale: 1, translateX: 0, translateY: 0 });
  }, [selectedDuplicateIndex]);

  const renderMapContent = () => {
    return (
      <Box sx={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%', 
        minHeight: '350px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Map Loading State */}
        {(mapLoading || loadingChainageSegment) && (
          <Box sx={{ 
            position: 'absolute', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: 'rgba(255,255,255,0.9)',
            p: 2,
            borderRadius: 2
          }}>
            <CircularProgress size={32} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {loadingChainageSegment ? 'Loading chainage segment...' : 'Loading map...'}
            </Typography>
          </Box>
        )}
        
        {/* Map Container */}
        <Box
          ref={mapContainerRef}
          sx={{
            width: '100%',
            height: '100%',
            minHeight: '350px',
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: '#f5f5f5',
            position: 'relative',
            '& .leaflet-container': {
              height: '100% !important',
              width: '100% !important',
              borderRadius: 1,
            },
            '& .leaflet-popup-content-wrapper': {
              borderRadius: '8px',
            },
            '& .leaflet-popup-tip': {
              backgroundColor: 'white',
            },
            '& .leaflet-control-zoom': {
              borderRadius: '6px',
              overflow: 'hidden',
            },
            '& .chainage-segment-popup': {
              '& .leaflet-popup-content-wrapper': {
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }
            }
          }}
        />
        
        {/* Map Legend */}
        {!mapLoading && (
          <Box sx={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            bgcolor: 'rgba(255,255,255,0.95)',
            p: 1.5,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            border: '1px solid rgba(0,0,0,0.1)',
            fontSize: '0.75rem',
            zIndex: 1000
          }}>
            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
              Legend
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Box sx={{ 
                width: 12, 
                height: 12, 
                borderRadius: '50%', 
                bgcolor: '#f44336',
                border: '2px solid white',
                boxShadow: '0 1px 3px rgba(244, 67, 54, 0.4)'
              }} />
              <Typography variant="caption">Original Defect</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Box sx={{ 
                width: 12, 
                height: 12, 
                borderRadius: '50%', 
                border: '2px dashed #f44336',
                bgcolor: 'rgba(244, 67, 54, 0.1)'
              }} />
              <Typography variant="caption">{distanceThreshold}m Threshold Zone</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Box sx={{ 
                width: 10, 
                height: 10, 
                borderRadius: '50%', 
                bgcolor: '#ff9800',
                border: '2px solid white',
                boxShadow: '0 1px 3px rgba(255, 152, 0, 0.4)'
              }} />
              <Typography variant="caption">Duplicate Defects ({duplicateImages?.length || 0})</Typography>
            </Box>
            {duplicateImages && duplicateImages.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Box sx={{ 
                  width: 12, 
                  height: 2, 
                  border: '1px dashed #9e9e9e',
                  bgcolor: 'transparent'
                }} />
                <Typography variant="caption">Connection Lines</Typography>
              </Box>
            )}
            {chainageSegmentData && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ 
                  width: 12, 
                  height: 8, 
                  border: '1px dashed #2196F3',
                  bgcolor: 'rgba(33, 150, 243, 0.1)'
                }} />
                <Typography variant="caption">Chainage Segment</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const getSecureImageUrl = React.useCallback((imagePath) => {
    if (!imagePath || imagePath === 'no-image') return null;

    // The backend provides a clean, web-relative path like 'uploads/...'.
    // We just need to normalize the slashes. The AuthenticatedImage component
    // handles the rest.
    const normalizedPath = imagePath.replace(/\\/g, '/');
    return normalizedPath;
  }, []);

  const renderOriginalImage = () => {
    if (!selectedRowForDuplicates) return null;

    const originalImagePath = getSecureImageUrl(
      selectedRowForDuplicates.rawData?.original_image_path ||
      selectedRowForDuplicates.rawData?.image_path ||
      selectedRowForDuplicates.image_path
    );

    const boundingBox = originalBoundingBox;

    if (!originalImagePath) {
      return (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'text.secondary',
          bgcolor: alpha(theme.palette.grey[100], 0.5),
          borderRadius: 2
        }}>
          <Typography variant="h4" sx={{ mb: 1, opacity: 0.3 }}>📷</Typography>
          <Typography variant="body2">Original image not available</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#f8f9fa',
        cursor: originalIsPanning ? 'grabbing' : (originalZoom.scale > 1 ? 'grab' : 'default'),
        overflow: 'hidden',
        borderRadius: 2
      }}
      ref={originalImageContainerRef}
      onWheel={originalHandleWheel}
      onMouseDown={(e) => {
        if (originalZoom.scale > 1) {
          originalHandlePanStart(e);
        }
      }}
      >
        <Box sx={{
          transform: `scale(${originalZoom.scale}) translate(${originalZoom.translateX / originalZoom.scale}px, ${originalZoom.translateY / originalZoom.scale}px)`,
          transformOrigin: '0 0',
          transition: originalIsPanning ? 'none' : 'transform 0.3s ease-out'
        }}>
        <AuthenticatedImage
            ref={originalImageRef}
          src={originalImagePath}
          alt="Original finding"
          sx={{
              maxWidth: '100%',
              maxHeight: '100%',
            objectFit: 'contain',
              display: 'block',
              userSelect: 'none'
          }}
        />
        
        {/* Bounding Box Overlay */}
          {boundingBox && boundingBox.x1 !== undefined && boundingBox.x1 !== null && originalImageRef.current && (
          <Box
            sx={{
              position: 'absolute',
              border: `2px solid ${theme.palette.error.main}`,
              borderRadius: '4px',
              backgroundColor: alpha(theme.palette.error.main, 0.1),
              pointerEvents: 'none',
              left: `${(boundingBox.x1 / (boundingBox.image_width || 1920)) * 100}%`,
              top: `${(boundingBox.y1 / (boundingBox.image_height || 1080)) * 100}%`,
              width: `${((boundingBox.x2 - boundingBox.x1) / (boundingBox.image_width || 1920)) * 100}%`,
              height: `${((boundingBox.y2 - boundingBox.y1) / (boundingBox.image_height || 1080)) * 100}%`,
            }}
          />
        )}
        </Box>
        
        {/* Loading indicator for bounding box */}
        {loadingOriginalBoundingBox && (
          <Box sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            bgcolor: 'rgba(0, 0, 0, 0.75)',
            color: 'white',
            px: 1,
            py: 0.5,
            borderRadius: '6px',
            fontSize: '0.7rem',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            zIndex: 10
          }}>
            <CircularProgress size={12} thickness={4} sx={{ color: 'white' }} />
            Loading box...
          </Box>
        )}
        
        {/* Original Image Info Badge */}
        <Chip
          label="Original"
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            bgcolor: theme.palette.primary.main,
            color: 'white',
            fontWeight: 600,
            fontSize: '0.75rem',
            zIndex: 10
          }}
        />

        {/* Zoom Controls */}
        <Box sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          zIndex: 10
        }}>
          <Button
            variant="contained"
            size="small"
            onClick={originalToggleZoom}
            startIcon={originalZoom.scale === 1 ? <ZoomInIcon fontSize="small" /> : <ZoomOutMapIcon fontSize="small" />}
            sx={{ 
              minWidth: 'auto',
              px: 1.5,
              fontSize: '0.75rem',
              bgcolor: originalZoom.scale === 1 ? 'rgba(255,255,255,0.9)' : 'rgba(33, 150, 243, 0.9)',
              color: originalZoom.scale === 1 ? theme.palette.primary.main : 'white',
              border: originalZoom.scale === 1 ? `1px solid ${theme.palette.primary.main}` : 'none',
              '&:hover': {
                bgcolor: originalZoom.scale === 1 ? 'rgba(255,255,255,1)' : 'rgba(33, 150, 243, 1)'
              }
            }}
          >
            {originalZoom.scale === 1 ? 'Zoom to Box' : 'Reset View'}
          </Button>
          
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.9)',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.75rem',
            color: theme.palette.text.secondary,
            textAlign: 'center'
          }}>
            {Math.round(originalZoom.scale * 100)}%
          </Box>
        </Box>

        {/* Help text when zoomed */}
        {originalZoom.scale > 1 && (
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            px: 2,
            py: 1,
            borderRadius: 2,
            fontSize: '0.75rem',
            textAlign: 'center',
            pointerEvents: 'none',
            opacity: originalIsPanning ? 0 : 0.8,
            transition: 'opacity 0.3s ease',
            zIndex: 5
          }}>
            Drag to pan • Scroll to zoom
          </Box>
        )}
      </Box>
    );
  };

  const renderSelectedDuplicateImage = () => {
    if (!selectedDuplicate) {
      return (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'text.secondary',
          bgcolor: alpha(theme.palette.grey[100], 0.5),
          borderRadius: 2
        }}>
          <Typography variant="h4" sx={{ mb: 1, opacity: 0.3 }}>🔍</Typography>
          <Typography variant="body2">Select a duplicate to view</Typography>
        </Box>
      );
    }

    const duplicateImagePath = getSecureImageUrl(selectedDuplicate.image_path);
    const boundingBox = {
      x1: selectedDuplicate.x1,
      y1: selectedDuplicate.y1,
      x2: selectedDuplicate.x2,
      y2: selectedDuplicate.y2,
      image_width: selectedDuplicate.image_width,
      image_height: selectedDuplicate.image_height
    };

    if (!duplicateImagePath) {
      return (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'text.secondary',
          bgcolor: alpha(theme.palette.grey[100], 0.5),
          borderRadius: 2
        }}>
          <Typography variant="h4" sx={{ mb: 1, opacity: 0.3 }}>📷</Typography>
          <Typography variant="body2">Duplicate image not available</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#f8f9fa',
        cursor: duplicateIsPanning ? 'grabbing' : (duplicateZoom.scale > 1 ? 'grab' : 'default'),
        overflow: 'hidden',
        borderRadius: 2
      }}
      ref={duplicateImageContainerRef}
      onWheel={duplicateHandleWheel}
      onMouseDown={(e) => {
        if (duplicateZoom.scale > 1) {
          duplicateHandlePanStart(e);
        }
      }}
      >
        <Box sx={{
          transform: `scale(${duplicateZoom.scale}) translate(${duplicateZoom.translateX / duplicateZoom.scale}px, ${duplicateZoom.translateY / duplicateZoom.scale}px)`,
          transformOrigin: '0 0',
          transition: duplicateIsPanning ? 'none' : 'transform 0.3s ease-out'
        }}>
        <AuthenticatedImage
            ref={duplicateImageRef}
          src={duplicateImagePath}
          alt={`Duplicate finding ${selectedDuplicateIndex + 1}`}
          sx={{
              maxWidth: '100%',
              maxHeight: '100%',
            objectFit: 'contain',
              display: 'block',
              userSelect: 'none'
          }}
        />

        {/* Bounding Box Overlay */}
          {boundingBox && boundingBox.x1 !== undefined && duplicateImageRef.current && (
          <Box
            sx={{
              position: 'absolute',
              border: `2px solid ${theme.palette.warning.main}`,
              borderRadius: '4px',
              backgroundColor: alpha(theme.palette.warning.main, 0.1),
              pointerEvents: 'none',
              left: `${(boundingBox.x1 / (boundingBox.image_width || 1920)) * 100}%`,
              top: `${(boundingBox.y1 / (boundingBox.image_height || 1080)) * 100}%`,
              width: `${((boundingBox.x2 - boundingBox.x1) / (boundingBox.image_width || 1920)) * 100}%`,
              height: `${((boundingBox.y2 - boundingBox.y1) / (boundingBox.image_height || 1080)) * 100}%`,
            }}
          />
        )}
        </Box>

        {/* Duplicate Image Info Badges */}
        <Chip
          label={`Duplicate #${selectedDuplicateIndex + 1}`}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            bgcolor: theme.palette.warning.main,
            color: 'white',
            fontWeight: 600,
            fontSize: '0.75rem',
            zIndex: 10
          }}
        />
        
        <Box sx={{
          position: 'absolute',
          bottom: 12,
          left: 12,
          bgcolor: 'rgba(0, 0, 0, 0.75)',
          color: 'white',
          px: 1.5,
          py: 0.5,
          borderRadius: '6px',
          fontSize: '0.8rem',
          fontWeight: 600,
          zIndex: 10
        }}>
          Confidence: {(selectedDuplicate.confidence_score * 100).toFixed(1)}% • Distance: {selectedDuplicate.distance_from_confirmed_defect_meters?.toFixed(1) || 'N/A'}m
        </Box>

        {/* Zoom Controls */}
        <Box sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          zIndex: 10
        }}>
          <Button
            variant="contained"
            size="small"
            onClick={duplicateToggleZoom}
            startIcon={duplicateZoom.scale === 1 ? <ZoomInIcon fontSize="small" /> : <ZoomOutMapIcon fontSize="small" />}
            sx={{ 
              minWidth: 'auto',
              px: 1.5,
              fontSize: '0.75rem',
              bgcolor: duplicateZoom.scale === 1 ? 'rgba(255,255,255,0.9)' : 'rgba(33, 150, 243, 0.9)',
              color: duplicateZoom.scale === 1 ? theme.palette.primary.main : 'white',
              border: duplicateZoom.scale === 1 ? `1px solid ${theme.palette.primary.main}` : 'none',
              '&:hover': {
                bgcolor: duplicateZoom.scale === 1 ? 'rgba(255,255,255,1)' : 'rgba(33, 150, 243, 1)'
              }
            }}
          >
            {duplicateZoom.scale === 1 ? 'Zoom to Box' : 'Reset View'}
          </Button>
          
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.9)',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.75rem',
            color: theme.palette.text.secondary,
            textAlign: 'center'
          }}>
            {Math.round(duplicateZoom.scale * 100)}%
          </Box>
        </Box>

        {/* Help text when zoomed */}
        {duplicateZoom.scale > 1 && (
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            px: 2,
            py: 1,
            borderRadius: 2,
            fontSize: '0.75rem',
            textAlign: 'center',
            pointerEvents: 'none',
            opacity: duplicateIsPanning ? 0 : 0.8,
            transition: 'opacity 0.3s ease',
            zIndex: 5
          }}>
            Drag to pan • Scroll to zoom
          </Box>
        )}
      </Box>
    );
  };

  // ============= ZOOM AND PAN FUNCTIONS FOR ORIGINAL IMAGE =============

  // Zoom to bounding box for original image
  const originalZoomToBox = () => {
    const imageElement = originalImageRef.current;
    const containerElement = originalImageContainerRef.current;
    
    if (!imageElement || !containerElement || !originalBoundingBox) return;
    
    const imageRect = imageElement.getBoundingClientRect();
    const containerRect = containerElement.getBoundingClientRect();
    
    // Calculate bounding box center in image coordinates
    const boxCenterX = (originalBoundingBox.x1 + originalBoundingBox.x2) / 2;
    const boxCenterY = (originalBoundingBox.y1 + originalBoundingBox.y2) / 2;
    
    // Calculate bounding box dimensions
    const boxWidth = originalBoundingBox.x2 - originalBoundingBox.x1;
    const boxHeight = originalBoundingBox.y2 - originalBoundingBox.y1;
    
    // Add padding around the box (20% on each side)
    const paddingFactor = 0.4; // 20% padding on each side = 40% total
    const paddedBoxWidth = boxWidth * (1 + paddingFactor);
    const paddedBoxHeight = boxHeight * (1 + paddingFactor);
    
    // Calculate scale to fit the padded box in the container
    const scaleX = containerRect.width / (paddedBoxWidth / (originalBoundingBox.image_width || 1920) * imageRect.width);
    const scaleY = containerRect.height / (paddedBoxHeight / (originalBoundingBox.image_height || 1080) * imageRect.height);
    const targetScale = Math.min(scaleX, scaleY, 4); // Cap at 4x zoom
    
    // Calculate translation to center the box
    const imageCenterX = imageRect.width / 2;
    const imageCenterY = imageRect.height / 2;
    const containerCenterX = containerRect.width / 2;
    const containerCenterY = containerRect.height / 2;
    
    // Convert box center to image element coordinates
    const boxCenterXInImage = (boxCenterX / (originalBoundingBox.image_width || 1920)) * imageRect.width;
    const boxCenterYInImage = (boxCenterY / (originalBoundingBox.image_height || 1080)) * imageRect.height;
    
    const translateX = containerCenterX - boxCenterXInImage * targetScale;
    const translateY = containerCenterY - boxCenterYInImage * targetScale;
    
    setOriginalZoom({
      scale: targetScale,
      translateX,
      translateY
    });
  };

  const originalResetZoom = () => {
    setOriginalZoom({ scale: 1, translateX: 0, translateY: 0 });
  };

  const originalToggleZoom = () => {
    if (originalZoom.scale === 1) {
      originalZoomToBox();
    } else {
      originalResetZoom();
    }
  };

  const originalHandleZoom = (delta, centerX, centerY) => {
    const zoomFactor = delta > 0 ? 1.2 : 1 / 1.2;
    const newScale = Math.max(0.5, Math.min(originalZoom.scale * zoomFactor, 5));
    
    if (newScale === originalZoom.scale) return;
    
    // Calculate new translation to zoom towards the cursor position
    const scaleChange = newScale / originalZoom.scale;
    const newTranslateX = centerX - (centerX - originalZoom.translateX) * scaleChange;
    const newTranslateY = centerY - (centerY - originalZoom.translateY) * scaleChange;
    
    setOriginalZoom({
      scale: newScale,
      translateX: newTranslateX,
      translateY: newTranslateY
    });
  };

  const originalHandleWheel = (event) => {
    if (!originalImageContainerRef.current) return;
    
    event.preventDefault();
    const rect = originalImageContainerRef.current.getBoundingClientRect();
    const centerX = event.clientX - rect.left;
    const centerY = event.clientY - rect.top;
    
    originalHandleZoom(-event.deltaY, centerX, centerY);
  };

  const originalHandlePanStart = (event) => {
    setOriginalIsPanning(true);
    setOriginalPanStart({
      x: event.clientX - originalZoom.translateX,
      y: event.clientY - originalZoom.translateY
    });
  };

  const originalHandlePanMove = (event) => {
    if (!originalIsPanning) return;
    
    setOriginalZoom(prev => ({
      ...prev,
      translateX: event.clientX - originalPanStart.x,
      translateY: event.clientY - originalPanStart.y
    }));
  };

  const originalHandlePanEnd = () => {
    setOriginalIsPanning(false);
  };

  // ============= ZOOM AND PAN FUNCTIONS FOR DUPLICATE IMAGE =============

  // Zoom to bounding box for duplicate image
  const duplicateZoomToBox = () => {
    const imageElement = duplicateImageRef.current;
    const containerElement = duplicateImageContainerRef.current;
    
    if (!imageElement || !containerElement || !selectedDuplicate) return;
    
    const imageRect = imageElement.getBoundingClientRect();
    const containerRect = containerElement.getBoundingClientRect();
    
    // Get bounding box from selected duplicate
    const boundingBox = {
      x1: selectedDuplicate.x1,
      y1: selectedDuplicate.y1,
      x2: selectedDuplicate.x2,
      y2: selectedDuplicate.y2,
      image_width: selectedDuplicate.image_width,
      image_height: selectedDuplicate.image_height
    };
    
    if (!boundingBox || boundingBox.x1 === undefined) return;
    
    // Calculate bounding box center in image coordinates
    const boxCenterX = (boundingBox.x1 + boundingBox.x2) / 2;
    const boxCenterY = (boundingBox.y1 + boundingBox.y2) / 2;
    
    // Calculate bounding box dimensions
    const boxWidth = boundingBox.x2 - boundingBox.x1;
    const boxHeight = boundingBox.y2 - boundingBox.y1;
    
    // Add padding around the box (20% on each side)
    const paddingFactor = 0.4; // 20% padding on each side = 40% total
    const paddedBoxWidth = boxWidth * (1 + paddingFactor);
    const paddedBoxHeight = boxHeight * (1 + paddingFactor);
    
    // Calculate scale to fit the padded box in the container
    const scaleX = containerRect.width / (paddedBoxWidth / (boundingBox.image_width || 1920) * imageRect.width);
    const scaleY = containerRect.height / (paddedBoxHeight / (boundingBox.image_height || 1080) * imageRect.height);
    const targetScale = Math.min(scaleX, scaleY, 4); // Cap at 4x zoom
    
    // Calculate translation to center the box
    const imageCenterX = imageRect.width / 2;
    const imageCenterY = imageRect.height / 2;
    const containerCenterX = containerRect.width / 2;
    const containerCenterY = containerRect.height / 2;
    
    // Convert box center to image element coordinates
    const boxCenterXInImage = (boxCenterX / (boundingBox.image_width || 1920)) * imageRect.width;
    const boxCenterYInImage = (boxCenterY / (boundingBox.image_height || 1080)) * imageRect.height;
    
    const translateX = containerCenterX - boxCenterXInImage * targetScale;
    const translateY = containerCenterY - boxCenterYInImage * targetScale;
    
    setDuplicateZoom({
      scale: targetScale,
      translateX,
      translateY
    });
  };

  const duplicateResetZoom = () => {
    setDuplicateZoom({ scale: 1, translateX: 0, translateY: 0 });
  };

  const duplicateToggleZoom = () => {
    if (duplicateZoom.scale === 1) {
      duplicateZoomToBox();
    } else {
      duplicateResetZoom();
    }
  };

  const duplicateHandleZoom = (delta, centerX, centerY) => {
    const zoomFactor = delta > 0 ? 1.2 : 1 / 1.2;
    const newScale = Math.max(0.5, Math.min(duplicateZoom.scale * zoomFactor, 5));
    
    if (newScale === duplicateZoom.scale) return;
    
    // Calculate new translation to zoom towards the cursor position
    const scaleChange = newScale / duplicateZoom.scale;
    const newTranslateX = centerX - (centerX - duplicateZoom.translateX) * scaleChange;
    const newTranslateY = centerY - (centerY - duplicateZoom.translateY) * scaleChange;
    
    setDuplicateZoom({
      scale: newScale,
      translateX: newTranslateX,
      translateY: newTranslateY
    });
  };

  const duplicateHandleWheel = (event) => {
    if (!duplicateImageContainerRef.current) return;
    
    event.preventDefault();
    const rect = duplicateImageContainerRef.current.getBoundingClientRect();
    const centerX = event.clientX - rect.left;
    const centerY = event.clientY - rect.top;
    
    duplicateHandleZoom(-event.deltaY, centerX, centerY);
  };

  const duplicateHandlePanStart = (event) => {
    setDuplicateIsPanning(true);
    setDuplicatePanStart({
      x: event.clientX - duplicateZoom.translateX,
      y: event.clientY - duplicateZoom.translateY
    });
  };

  const duplicateHandlePanMove = (event) => {
    if (!duplicateIsPanning) return;
    
    setDuplicateZoom(prev => ({
      ...prev,
      translateX: event.clientX - duplicatePanStart.x,
      translateY: event.clientY - duplicatePanStart.y
    }));
  };

  const duplicateHandlePanEnd = () => {
    setDuplicateIsPanning(false);
  };

  // ============= END ZOOM AND PAN FUNCTIONS =============

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="duplication-modal-title"
      aria-describedby="duplication-modal-description"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
        maxWidth: 1200,
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        borderRadius: '12px',
        boxShadow: 24,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Sticky Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          py: 1.5,
          px: 2.5,
          flexShrink: 0,
          bgcolor: 'background.paper'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon color="primary" fontSize="small" /> 
            <Typography id="duplication-modal-title" variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              Duplicate Findings
            </Typography>
            {selectedRowForDuplicates && (
              <Chip 
                label={`ID: ${selectedRowForDuplicates.recordType === 'confirmed_defect' ? `D-${selectedRowForDuplicates.defectId}` : `F-${selectedRowForDuplicates.id}`}`}
                icon={selectedRowForDuplicates.recordType === 'confirmed_defect' ? 
                  <BugReportIcon fontSize="small" /> : 
                  <HelpIcon fontSize="small" />
                }
                size="small" 
                variant="outlined"
                sx={{ ml: 1, fontSize: '0.7rem' }}
              />
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Main Content Area */}
        <Box sx={{
          display: 'flex',
          minHeight: 400,
          maxHeight: 'calc(90vh - 120px)',
        }}>
          {/* Left Side - Original Image */}
          <Box sx={{ 
            width: '50%',
            display: 'flex',
            flexDirection: 'column',
            borderRight: `1px solid ${alpha(theme.palette.divider, 0.12)}`
          }}>
            {/* Original Image Header */}
            <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`, bgcolor: alpha(theme.palette.grey[50], 0.5), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ fontSize: '0.875rem' }}>
                  {currentView === 'image' ? 'Original Image' : 'Location Map'}
                </Typography>
                {selectedRowForDuplicates && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {selectedRowForDuplicates.defectType} • {selectedRowForDuplicates.deviceId}
                  </Typography>
                )}
              </Box>
              
              {/* View Toggle */}
              <ToggleButtonGroup
                value={currentView}
                exclusive
                onChange={handleViewChange}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.grey[100], 0.8),
                  borderRadius: '8px',
                  p: 0.25,
                  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                  '& .MuiToggleButton-root': {
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    border: 'none',
                    borderRadius: '6px',
                    color: theme.palette.text.secondary,
                    backgroundColor: 'transparent',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.main,
                    },
                    '&.Mui-selected': {
                      bgcolor: 'white',
                      color: theme.palette.primary.main,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: 'white',
                        color: theme.palette.primary.main,
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="image" aria-label="image view">
                  <ImageIcon fontSize="small" sx={{ mr: 0.5 }} />
                  Image
                </ToggleButton>
                <ToggleButton value="map" aria-label="map view">
                  <MapIcon fontSize="small" sx={{ mr: 0.5 }} />
                  Map
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Original Image Content */}
            <Box sx={{ 
              minHeight: 350,
              maxHeight: 500,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {currentView === 'image' ? renderOriginalImage() : renderMapContent()}
            </Box>

            {/* Original Image Footer */}
            <Box sx={{ 
              px: 2, 
              py: 1,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              bgcolor: alpha(theme.palette.grey[50], 0.5),
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              {selectedRowForDuplicates && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
                  {currentView === 'image' ? (
                    <>
                      {selectedRowForDuplicates.location} • {formatDateTime && formatDateTime(selectedRowForDuplicates.detectedAt)}
                      {originalBoundingBox?.image_width && originalBoundingBox?.image_height && (
                        <> • {originalBoundingBox.image_width} × {originalBoundingBox.image_height} px</>
                      )}
                    </>
                  ) : (
                    <>
                      {formatDateTime && formatDateTime(selectedRowForDuplicates.detectedAt)} • 
                      {originalBoundingBox?.latitude && originalBoundingBox?.longitude && (
                        <> Lat: {originalBoundingBox.latitude.toFixed(6)}, Lng: {originalBoundingBox.longitude.toFixed(6)}</>
                      )}
                      {selectedRowForDuplicates?.location && (
                        <> • {selectedRowForDuplicates.location}</>
                      )}
                    </>
                  )}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Right Side - Selected Duplicate and Carousel */}
          <Box sx={{ 
            width: '50%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Selected Duplicate Image Section */}
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`
            }}>
              {/* Selected Duplicate Header */}
              <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`, bgcolor: alpha(theme.palette.grey[50], 0.5) }}>
                <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ fontSize: '0.875rem' }}>
                  Selected Duplicate {selectedDuplicate ? `#${selectedDuplicateIndex + 1}` : ''}
                </Typography>
                {selectedDuplicate && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {selectedDuplicate.device_name || selectedDuplicate.device_uuid} • {(selectedDuplicate.confidence_score * 100).toFixed(0)}% confidence • {selectedDuplicate.distance_from_confirmed_defect_meters?.toFixed(1) || 'N/A'}m distance
                    {(selectedDuplicate.detected_at || selectedDuplicate.created_at || selectedDuplicate.first_detected_at) && (
                      <> • 📅 {formatDateTime && formatDateTime(selectedDuplicate.detected_at || selectedDuplicate.created_at || selectedDuplicate.first_detected_at)}</>
                    )}
                  </Typography>
                )}
              </Box>

              {/* Selected Duplicate Content */}
              <Box sx={{ 
                flex: 1, 
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 200
              }}>
                {renderSelectedDuplicateImage()}
              </Box>
              
              {/* Selected Duplicate Footer */}
              {selectedDuplicate && (
                <Box sx={{ 
                  px: 2, 
                  py: 1,
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                  bgcolor: alpha(theme.palette.grey[50], 0.5),
                  flexShrink: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
                    {(selectedDuplicate.detected_at || selectedDuplicate.created_at || selectedDuplicate.first_detected_at) && 
                      formatDateTime && formatDateTime(selectedDuplicate.detected_at || selectedDuplicate.created_at || selectedDuplicate.first_detected_at)
                    }
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Duplicate Images Carousel Section */}
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0
            }}>
              {/* Carousel Header */}
              <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`, bgcolor: alpha(theme.palette.grey[50], 0.5) }}>
                <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ fontSize: '0.875rem' }}>
                  All Duplicates ({duplicateImages.length})
                </Typography>
              </Box>

              {/* Carousel Content */}
              <Box sx={{ p: 2, pb: 2.5 }}>
                {loadingDuplicates ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 90 }}>
                    <CircularProgress size={24} thickness={4} />
                    <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.75rem' }}>
                      Loading...
                    </Typography>
                  </Box>
                ) : duplicateImages.length > 0 ? (
                  <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', overflowY: 'visible', py: 0.5, pb: 1.5, px: 1 }}>
                    {duplicateImages.map((duplicate, index) => (
                      <Card 
                        key={duplicate.id}
                        sx={{ 
                          minWidth: 90,
                          width: 90,
                          height: 105,
                          cursor: 'pointer',
                          border: selectedDuplicateIndex === index ? `2px solid ${theme.palette.primary.main}` : '1px solid transparent',
                          transition: 'all 0.2s ease',
                          borderRadius: 1,
                          flexShrink: 0,
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: theme.shadows[2]
                          }
                        }}
                        onClick={() => setSelectedDuplicateIndex(index)}
                      >
                        <Box sx={{ position: 'relative', height: '60px', bgcolor: alpha(theme.palette.grey[100], 0.5) }}>
                          <AuthenticatedImage
                            src={getSecureImageUrl(duplicate.image_path)}
                            alt={`Duplicate ${index + 1}`}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          
                          {/* Thumbnail number badge */}
                          <Box sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            borderRadius: '50%',
                            width: 18,
                            height: 18,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            fontWeight: 600
                          }}>
                            {index + 1}
                          </Box>
                        </Box>
                        <Box sx={{ p: 0.5, textAlign: 'center', height: '45px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                            {(duplicate.confidence_score * 100).toFixed(0)}%
                          </Typography>
                          {(duplicate.detected_at || duplicate.created_at || duplicate.first_detected_at) && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', mt: 0.25, lineHeight: 1 }}>
                              {new Date(duplicate.detected_at || duplicate.created_at || duplicate.first_detected_at).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                              })}
                            </Typography>
                          )}
                        </Box>
                      </Card>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 90 }}>
                    <Typography variant="h6" sx={{ mb: 0.5, opacity: 0.3, fontSize: '1.2rem' }}>🔍</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      No duplicates found
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Sticky Footer */}
        <Box sx={{
          px: 2.5,
          py: 1.5,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          display: 'flex',
          justifyContent: 'flex-end',
          flexShrink: 0,
          bgcolor: 'background.paper'
        }}>
          <Button 
            onClick={onClose} 
            color="inherit"
            size="medium"
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default DuplicationModal; 